import { useCallback, useState } from 'react'

import { isDocxFile } from '@/hooks/documents/useDocxImport'
import type { DocxVersionSource } from '@/hooks/files/types'

import type { File as SweetFile, FileType } from '@/types/File'

const DEFAULT_DOCX_PREVIEW_FILE_TYPES: readonly FileType[] = ['pursuit_file']

interface UseDocxFilePreviewArgs {
  /** Null while the surface is still fetching it. */
  file: SweetFile | null | undefined
  /** The version being read, if the surface reads one; the file as it stands otherwise. */
  version?: DocxVersionSource | null
  /** Whether this surface offers the Word viewer at all. */
  enabled?: boolean
  /** File types this surface shows as Word documents. */
  fileTypes?: readonly FileType[] | 'all'
}

interface UseDocxFilePreviewResult {
  /** Show the file itself rather than the PDF rendition of it. */
  showDocxViewer: boolean
  /** Hand to the viewer: a document that will not open falls back to the rendition or download. */
  onLoadError: (error: Error) => void
}

/**
 * Whether a file should be previewed as the Word document it is. Viewing is
 * read-only, so what gates it is the file types the surface offers it on.
 */
export const useDocxFilePreview = ({
  file,
  version = null,
  enabled = true,
  fileTypes = DEFAULT_DOCX_PREVIEW_FILE_TYPES,
}: UseDocxFilePreviewArgs): UseDocxFilePreviewResult => {
  const isCandidate =
    enabled &&
    file != null &&
    // A version has no rendition, so the Word viewer is the only way to read it.
    (version != null || fileTypes === 'all' || fileTypes.includes(file.type)) &&
    isDocxFile(file)

  // Keyed by document rather than a flag: surfaces keep one viewer instance and
  // change the document under it. The version is part of the key so bytes
  // uploaded to replace ones that would not open get their own try.
  const documentKey = `${file?.id ?? ''}:${
    version?.id ?? file?.current_version_id ?? ''
  }`
  const [failedDocumentKey, setFailedDocumentKey] = useState<string | null>(
    null,
  )
  const onLoadError = useCallback(() => {
    setFailedDocumentKey(documentKey)
  }, [documentKey])

  return {
    showDocxViewer: isCandidate && failedDocumentKey !== documentKey,
    onLoadError,
  }
}
